import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { useLanguage, useSEO } from "../context/LanguageContext";
import { KineticLines, Reveal, Overline, SectionHeading, MarqueeBand, CTAButton } from "../components/Kinetic";

const HERO_IMG =
  "https://images.unsplash.com/photo-1772461215864-21dc3ede9757?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtaXN0eSUyMGZvcmVzdCUyMHJ1cmFsfGVufDB8fHx8MTc4ODEwNjI5Mnww&ixlib=rb-4.1.0&q=85";
const CENTRE_IMG =
  "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBjbGVhbnxlbnwwfHx8fDE3ODgxMDYyOTJ8MA&ixlib=rb-4.1.0&q=85";

const HomePage = () => {
  const { t } = useLanguage();
  useSEO(t("home.metaTitle"), t("home.metaDesc"));
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 180]);
  const pillars = t("home.pillars");
  const bridgeItems = t("home.bridgeItems");
  const timelineItems = t("home.timelineItems");

  return (
    <div data-testid="home-page">
      <section className="relative flex min-h-screen items-end overflow-hidden bg-graphite">
        <motion.img
          src={HERO_IMG}
          alt=""
          style={{ y: yBg }}
          className="absolute inset-0 h-[115%] w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-graphite/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-graphite via-transparent to-graphite/40" />
        <div className="absolute inset-0 structural-grid-light" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-28 pt-48">
          <Reveal y={16}>
            <Overline light>{t("home.heroOverline")}</Overline>
          </Reveal>
          <h1
            className="mt-8 font-display font-light text-5xl md:text-6xl lg:text-[5.5rem] tracking-tighter leading-[1.02] text-white"
            data-testid="home-hero-title"
          >
            <KineticLines lines={t("home.heroLines")} delay={0.25} />
          </h1>
          <Reveal delay={0.9}>
            <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-white/75">
              {t("home.heroText")}
            </p>
          </Reveal>
          <Reveal delay={1.05}>
            <div className="mt-10 flex flex-wrap gap-4">
              <CTAButton to="/mission" variant="light" testid="hero-cta-vision">
                {t("home.ctaVision")}
              </CTAButton>
              <CTAButton to="/scientifiques" variant="outlineLight" testid="hero-cta-join">
                {t("home.ctaJoin")}
              </CTAButton>
            </div>
          </Reveal>
        </div>
        <motion.div
          className="absolute bottom-8 right-8 hidden text-white/50 md:block"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="h-5 w-5" />
        </motion.div>
      </section>

      <MarqueeBand items={t("home.marquee")} />

      <section className="border-b border-graphite/10 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("home.visionOverline")} title={t("home.visionTitle")} />
          <div className="mt-16 grid gap-px bg-graphite/10 md:grid-cols-3">
            {pillars.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.12} className="group bg-bone p-8 md:p-10 transition-colors duration-300 hover:bg-forest">
                <p className="font-mono text-sm text-forest transition-colors duration-300 group-hover:text-white/60">{p.num}</p>
                <h3 className="mt-6 font-display text-2xl md:text-3xl font-light tracking-tight text-graphite transition-colors duration-300 group-hover:text-white">
                  {p.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-graphite-soft transition-colors duration-300 group-hover:text-white/70">
                  {p.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-graphite py-24 text-white md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2">
          <SectionHeading overline={t("home.bridgeOverline")} title={t("home.bridgeTitle")} light />
          <ul>
            {bridgeItems.map((item, i) => (
              <Reveal key={item} delay={i * 0.05} y={14}>
                <li className="group flex items-center justify-between border-b border-white/10 py-4 transition-colors duration-200 hover:border-white/30">
                  <span className="text-base md:text-lg text-white/80 transition-colors duration-200 group-hover:text-white">
                    {item}
                  </span>
                  <span className="font-mono text-xs text-white/30 transition-colors duration-200 group-hover:text-[#4ADE80]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal className="relative">
            <div className="overflow-hidden">
              <motion.img
                src={CENTRE_IMG}
                alt={t("home.centreTitle")}
                loading="lazy"
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 hidden h-full w-full border border-forest/30 md:block" style={{ zIndex: -1 }} />
          </Reveal>
          <div>
            <Reveal y={16}>
              <span className="inline-block border border-forest/30 bg-forest-soft px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-forest" data-testid="home-centre-status">
                {t("home.centreOverline")}
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05] text-graphite">
                {t("home.centreTitle")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-graphite-soft">{t("home.centreText")}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8">
                <CTAButton to="/centre" testid="home-centre-cta">
                  {t("home.centreCta")}
                </CTAButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-graphite/10 bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("home.timelineOverline")} title={t("home.timelineTitle")} />
          <div className="mt-14">
            {timelineItems.map((item, i) => (
              <Reveal key={item.date} delay={i * 0.1} y={14}>
                <div className="group flex flex-col gap-2 border-b border-graphite/10 py-6 transition-colors duration-200 hover:border-forest/40 md:flex-row md:items-baseline md:gap-12">
                  <span className="w-48 shrink-0 font-mono text-sm uppercase tracking-[0.15em] text-forest">{item.date}</span>
                  <span className="text-base md:text-lg text-graphite">{item.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10">
              <CTAButton to="/histoire" variant="outline" testid="home-timeline-cta">
                {t("home.timelineCta")}
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-28 md:py-40">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal y={16}>
            <Overline>{t("home.manifestoOverline")}</Overline>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 font-display text-3xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.15] text-graphite" data-testid="home-manifesto">
              {t("brand.message")}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-graphite-soft">{t("brand.messageNote")}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-forest py-24 text-white md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline="Green Horizon Project" title={t("home.ctaTitle")} text={t("home.ctaText")} light />
          <Reveal delay={0.25}>
            <div className="mt-12 flex flex-wrap gap-4">
              <CTAButton to="/scientifiques" variant="light" testid="home-cta-network">
                {t("home.cta1")}
              </CTAButton>
              <CTAButton to="/partenariats" variant="outlineLight" testid="home-cta-partner">
                {t("home.cta2")}
              </CTAButton>
              <CTAButton to="/soutenir" variant="outlineLight" testid="home-cta-support">
                {t("home.cta3")}
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
